import { useEffect, useRef, useState } from "react";

export function useTouchCapability(): boolean {
  const [touchCapable, setTouchCapable] = useState(false);
  useEffect(() => {
    const coarse = window.matchMedia("(pointer: coarse)");
    const update = () => setTouchCapable(coarse.matches || navigator.maxTouchPoints > 0 || "ontouchstart" in window);
    update();
    coarse.addEventListener?.("change", update);
    return () => coarse.removeEventListener?.("change", update);
  }, []);
  return touchCapable;
}

export function VirtualJoystick({ onVector, label = "Movement joystick", className = "" }: {
  onVector: (x: number, y: number, active: boolean) => void;
  label?: string;
  className?: string;
}) {
  const touchCapable = useTouchCapability();
  const baseRef = useRef<HTMLDivElement>(null);
  const onVectorRef = useRef(onVector);
  const activePointer = useRef<number | null>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });

  useEffect(() => { onVectorRef.current = onVector; }, [onVector]);

  const update = (clientX: number, clientY: number) => {
    const base = baseRef.current;
    if (!base) return;
    const rect = base.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const limit = rect.width * .31;
    let dx = clientX - centerX;
    let dy = clientY - centerY;
    const length = Math.hypot(dx, dy);
    if (length > limit) {
      dx = dx / length * limit;
      dy = dy / length * limit;
    }
    setKnob({ x: dx, y: dy });
    onVectorRef.current(dx / limit, dy / limit, true);
  };

  const release = (element?: HTMLDivElement) => {
    if (activePointer.current !== null && element?.hasPointerCapture(activePointer.current)) element.releasePointerCapture(activePointer.current);
    activePointer.current = null;
    setKnob({ x: 0, y: 0 });
    onVectorRef.current(0, 0, false);
  };

  useEffect(() => () => onVectorRef.current(0, 0, false), []);
  if (!touchCapable) return null;

  return <div
    ref={baseRef}
    className={`virtual-joystick ${className}`}
    role="application"
    aria-label={label}
    onPointerDown={(event) => {
      event.preventDefault();
      activePointer.current = event.pointerId;
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Some embedded browsers do not expose active pointer capture. */ }
      update(event.clientX, event.clientY);
    }}
    onPointerMove={(event) => {
      if (activePointer.current !== event.pointerId) return;
      event.preventDefault();
      update(event.clientX, event.clientY);
    }}
    onPointerUp={(event) => release(event.currentTarget)}
    onPointerCancel={(event) => release(event.currentTarget)}
    onLostPointerCapture={() => release()}
  >
    <div className="joystick-rings" />
    <div className="joystick-knob" style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }}><span>✦</span></div>
  </div>;
}
